import { TFunction } from 'i18next';
import { AddonServiceLine } from '../../utils';
import { PriceBreakdown } from '../../api/booking/pricePreviewApiTypes';
import { getCurrentLang } from '../../i18n/i18n';

// i18n keys for the non-service (base) price lines returned by the BE breakdown.
// Reuses the existing reservation_summary labels (weekday/weekend/nights/duration).
const BASE_LABEL_KEYS: Record<string, string> = {
    weekday: 'booking_details.reservation_summary.weekday',
    weekend: 'booking_details.reservation_summary.weekend',
    daily: 'booking_details.reservation_summary.nights',
    // Collapsed stay line (#1740) — same label the view mode uses for the same
    // amount, and correct for a venue, which has no nights.
    base: 'booking_details.reservation_summary.base_price',
    duration: 'booking_details.reservation_summary.duration',
    // #1713 sub-day units — the count is hours resp. half-days.
    hour: 'booking_details.reservation_summary.hour',
    half_day: 'booking_details.reservation_summary.half_day',
};

/**
 * Maps a backend PriceBreakdown's `lines` into the `AddonServiceLine` row shape
 * the summary components already render (#1203). Base lines (weekday / weekend /
 * daily / duration) get a translated label with their unit count; service lines
 * use the backend-localized service name and their chosen quantity. This lets
 * every price line — base rate + add-ons — render uniformly from one BE source.
 */
export const breakdownToServiceLines = (
    breakdown: PriceBreakdown,
    t: TFunction,
): AddonServiceLine[] =>
    breakdown.lines.map((line) => {
        if (line.key === 'service') {
            // Prefer the current-language service name; fall back to the raw
            // backend label (service_name) only if no localized value exists.
            const localized = line.langNames?.[getCurrentLang()];
            return {
                name: localized || line.label,
                quantity: line.quantity && line.quantity > 1 ? line.quantity : undefined,
                amount: line.amount,
            };
        }
        const base = t(BASE_LABEL_KEYS[line.key] ?? line.key);
        return {
            name: line.count != null ? `${base} (${line.count})` : base,
            amount: line.amount,
        };
    });
